import React, { useState, useEffect, useMemo } from "react";
import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  PDFViewer,
  Font,
} from "@react-pdf/renderer";
import EditarCotizacionForm from "./EditarCotizacionForm";
import { deleteDoc, getDoc, doc, updateDoc } from "firebase/firestore";
import { db, storage } from "../firebaseConfig";
import { ref, deleteObject } from "firebase/storage";
import Modal from "react-modal";
import Swal from "sweetalert2";
import OpenRegular from "../fonts/OpenSans-Regular.ttf";
import OpenBold from "../fonts/OpenSans-Bold.ttf";
import { useNavigate } from "react-router-dom";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";

Font.register({
  family: "Open",
  fonts: [
    { src: OpenRegular, fontWeight: "normal" },
    { src: OpenBold, fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  pdfContainer: {
    position: "fixed",
    inset: 0, // equivale a top/right/bottom/left: 0
    padding: "2vw",
    backgroundColor: "rgba(0,0,0,0.6)",
    zIndex: 1000,
    display: "grid",
    placeItems: "center",
  },
  pdfViewer: {
    width: "min(96vw, 1200px)",
    height: "min(94dvh, 1000px)", // dvh = viewport dinámico (mejor en móviles)
    border: "1px solid #ccc",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#fff",
    boxShadow: "0 10px 30px rgba(0,0,0,.25)",
  },
  page: {
    paddingTop: 30,
    paddingRight: 30,
    paddingLeft: 30,
    paddingBottom: 30,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: "20px",
    marginTop: "20px",
  },
  logoContainer: { width: "50%", marginTop: "5px" },
  logo: { width: "150px", height: "auto" },
  headerInfo: { width: "50%", textAlign: "right" },
  companyName: { fontSize: "10px", fontWeight: 600, fontFamily: "Open" },
  companyDetails: { fontSize: "10px", fontFamily: "Open", marginTop: "2px" },
  titleSection: { textAlign: "center", marginBottom: "20px" },
  title: { fontSize: "18px", fontWeight: "bold", color: "#1976d2" },
  infoContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: "20px",
  },
  clientInfo: { width: "50%", fontSize: "10px" },
  infoTitle: { fontSize: "10px", fontFamily: "Open", marginBottom: "5px" },
  infoText: { fontSize: "10px", fontFamily: "Open", marginBottom: "3px" },
  infoTextColor: {
    fontSize: "10px",
    fontFamily: "Open",
    marginBottom: "3px",
    color: "#1976d2",
    fontWeight: 600,
  },
  infoBlessed: {
    fontSize: "11px",
    marginTop: "10px",
    fontFamily: "Open",
    textAlign: "left",
    marginBottom: "10px",
  },
  cotizacionTable: {
    width: "50%",
    textAlign: "right",
    marginTop: "-10px",
    left: "40px",
  },
  table: {
    marginBottom: "10px",
    fontFamily: "Open",
    width: "100%",
    borderWidth: 1,
    borderColor: "#fff",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#fff",
    backgroundColor: "#F9F9F9",
  },
  tableRowHeader: {
    flexDirection: "row",
    backgroundColor: "#6c6c6c",
    borderBottomWidth: 1,
    borderColor: "#fff",
    color: "#fff",
    fontSize: "10px",
  },
  tableCell: {
    padding: 8,
    textAlign: "left",
    borderRightWidth: 1,
    borderRightColor: "#fff",
    borderRightStyle: "solid",
    fontSize: "11px",
    lineHeight: "1.5",
  },
  tableCellDesc: {
    padding: 8,
    textAlign: "left",
    borderRightWidth: 1,
    borderRightColor: "#fff",
    borderRightStyle: "solid",
    fontSize: "10px",
    lineHeight: "1.5",
    color: "#7E7D7D",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },
  closeButton: { color: "#fff", top: "15px", fontSize: "20px" },
  tableSmall: {
    marginTop: "10px",
    width: "30%",
    alignSelf: "flex-end",
    marginRight: "-20px",
  },
  tableRowSmall: {
    display: "flex",
    flexDirection: "row",
    marginBottom: "3px",
    alignItems: "stretch",
  },
  tableLabelSmall: {
    fontSize: "9px",
    fontWeight: "bold",
    width: "30%",
    padding: "6px",
    color: "#fff",
    backgroundColor: "#6c6c6c",
    textAlign: "left",
  },
  tableValueSmall: {
    fontSize: "9px",
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "70%",
    textAlign: "left",
    width: "70%",
    padding: 6,
    paddingRight: 12,
    margin: 0,
    lineHeight: 1.35,
  },
  footerContainer: {
    marginTop: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "flex-start",
    minHeight: 0,
  },
  footerLeft: { width: "45%", textAlign: "justify", left: 0 },
  footerRight: { width: "40%", textAlign: "right" },
  footerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerRowGray: { backgroundColor: "#F9F9F9", padding: 8 },
  footerRowBlue: { backgroundColor: "#1565c0", padding: 8 },
  footerLabel: { fontSize: 11, textAlign: "right" },
  footerValue: { fontSize: 11, textAlign: "right" },
  footerLabelBlue: { color: "#fff", fontWeight: "bold" },
  footerValueBlue: { color: "#fff", fontWeight: "bold", margin: 0 },
  condicionesTitle: {
    fontSize: "10px",
    marginBottom: 15,
    marginTop: 10,
    fontFamily: "Open",
  },
  termsContainer: { gap: 10, textAlign: "justify", fontFamily: "Open" },
  numeroDePagina: { color: "#999999", fontSize: 12 },
  condicionesContainer: {
    marginBottom: 10,
    padding: 10,
    borderWidth: 1,
    borderColor: "#000",
  },
  condicionesTitulo: {
    fontSize: 12,
    fontWeight: 600,
    color: "red",
    marginBottom: 5,
    fontFamily: "Open",
  },
  condicionesTexto: {
    fontSize: 11,
    marginBottom: 3,
    fontFamily: "Open",
    textAlign: "justify",
  },
  condicionesTexto2: {
    fontSize: 11,
    marginBottom: 3,
    fontFamily: "Open",
    textAlign: "justify",
  },
  espaciado: { marginBottom: 20 },
  cancelacionesContainer: {
    marginBottom: 10,
    padding: 10,
    borderWidth: 1,
    borderColor: "#000",
  },
  cancelacionesTitulo: {
    fontFamily: "Open",
    fontSize: 12,
    fontWeight: "bold",
    color: "red",
    marginBottom: 5,
    marginTop: 15,
  },
  cancelacionesTexto: {
    fontSize: 11,
    marginBottom: 5,
    textAlign: "justify",
    fontFamily: "Open",
  },
  garantiaContainer: {
    marginBottom: 10,
    padding: 10,
    borderWidth: 1,
    borderColor: "#000",
  },
  garantiaTexto: {
    fontSize: 11,
    textAlign: "justify",
    marginBottom: 3,
    fontFamily: "Open",
  },
  bancariosContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "stretch",
    padding: 10,
    borderWidth: 1,
    margin: 0,
    borderColor: "#000",
    height: 120,
  },
  datosBancariosIzquierda: { width: "48%", gap: 5 },
  datosBancariosDerecha: { width: "48%", textAlign: "left" },
  lineaDivisoria: {
    width: 1,
    backgroundColor: "#000",
    height: "119px",
    top: -10,
  },
  datosBancariosTitulo: {
    fontSize: 12,
    fontWeight: "bold",
    fontFamily: "Open",
    marginBottom: 5,
  },
  banco: { fontSize: 11, fontFamily: "Open", marginBottom: 3 },
  moneda: { fontSize: 11, marginBottom: 3, fontFamily: "Open" },
  beneficiario: { fontSize: 11, fontFamily: "Open", marginBottom: 3 },
  cuenta: { fontSize: 11, fontFamily: "Open", marginBottom: 3 },
  clabe: { fontSize: 11, fontFamily: "Open", marginBottom: 3 },
  referencias: {
    fontSize: 11,
    color: "red",
    marginBottom: 5,
    fontFamily: "Open",
  },
  envioComprobante: {
    fontSize: 11,
    color: "red",
    fontFamily: "Open",
    marginBottom: 3,
  },
  correos: { fontSize: 11, marginBottom: 2, fontFamily: "Open" },
  footerBlock: { marginTop: 10, width: "100%" },
  montoEnLetrasLabel: {
    fontFamily: "Open",
    fontWeight: "bold",
    fontSize: 9,
    textAlign: "right",
    paddingRight: 5,
    marginTop: 4,
    color: "#666",
  },
  montoEnLetras: {
    fontFamily: "Open",
    fontWeight: "bold",
    fontSize: 9,
    marginTop: 0,
    textAlign: "right",
    paddingRight: 5,
  },
  tableLabelBox: {
    width: "30%",
    backgroundColor: "#6c6c6c",
    padding: 6,
    justifyContent: "center",
  },
  tableLabelSmallText: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#fff",
    textAlign: "left",
  },
  rowTight: {
    marginBottom: 3,
  },
  labelTightBoxSm: {
    paddingTop: 2,
    paddingBottom: 2,
  },
  valueTightSm: {
    paddingTop: 2,
    paddingBottom: 3,
    lineHeight: 1.2,
  },
  montoGroup: { alignItems: "flex-end", marginTop: 22, paddingRight: 5 },
  descTitle: { fontSize: 11, color: "#000" },
  descExtra: { fontSize: 10, color: "#7E7D7D", marginTop: 2 },
  cellTop: { alignSelf: "flex-start" },
  sectionBlock: { marginTop: 56, fontFamily: "Open" },
  sectionTitle: { fontSize: 12, fontWeight: "bold", marginBottom: 8 },
  footerLabelContainer: { flexGrow: 1 },
  footerValueContainer: { minWidth: 120, alignItems: "flex-end" },
  sectionHeading: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 6,
  },
  sectionItem: { fontSize: 11, marginBottom: 4 },
  entregaBox: {
    width: "58%", // Ajusta 56–60% si quieres mover el “corte” fino
    alignSelf: "flex-start",
    paddingRight: 8,
  },
  entregaList: {
    marginTop: 2,
  },
  entregaLine: {
    fontSize: 11,
    lineHeight: 1.35,
    marginBottom: 4,
    textAlign: "left",
    fontFamily: "Open",
  },
  totalsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
  },
  lettersRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    marginTop: 6, // separación pequeña entre filas
  },
  montoGroupRight: {
    width: "40%",
    alignItems: "flex-end",
    paddingRight: 5,
  },
  sectionBlockReset: { marginTop: 0 },
});

const partirEnDosLineas = (
  s = "",
  { minLen = 28, preferidas = ["de", "para", "en", "con", "por", "del"] } = {}
) => {
  const text = String(s).replace(/\s+/g, " ").trim();
  if (!text || text.length <= minLen) return text;

  const words = text.split(" ");
  // posiciones acumuladas después de cada palabra (incluyendo un espacio)
  const total = text.length;
  const target = Math.round(total / 2);

  let pos = 0;
  let mejorIdx = -1;
  let mejorDiff = Infinity;
  const candidatos = [];

  for (let i = 0; i < words.length - 1; i++) {
    pos += words[i].length + 1; // +1 por el espacio
    const diff = Math.abs(pos - target);
    const esPreferida = preferidas.includes(words[i].toLowerCase());
    candidatos.push({ i, diff, esPreferida });
    if (diff < mejorDiff) {
      mejorDiff = diff;
      mejorIdx = i;
    }
  }

  // prioriza conectores cerca del centro
  const preferido = candidatos
    .filter((c) => c.esPreferida)
    .sort((a, b) => a.diff - b.diff)[0];

  const idx = preferido ? preferido.i : mejorIdx;
  if (idx < 0) return text;

  const primera = words.slice(0, idx + 1).join(" ");
  const segunda = words.slice(idx + 1).join(" ");
  return `${primera}\n${segunda}`;
};

/* ===== Helpers números → letras ===== */
const numeroATexto = (n) => {
  n = Math.floor(Math.abs(n));
  if (n === 0) return "CERO";
  const U = [
    "",
    "UNO",
    "DOS",
    "TRES",
    "CUATRO",
    "CINCO",
    "SEIS",
    "SIETE",
    "OCHO",
    "NUEVE",
  ];
  const E = {
    10: "DIEZ",
    11: "ONCE",
    12: "DOCE",
    13: "TRECE",
    14: "CATORCE",
    15: "QUINCE",
    16: "DIECISÉIS",
    17: "DIECISIETE",
    18: "DIECIOCHO",
    19: "DIECINUEVE",
  };
  const D = {
    20: "VEINTE",
    30: "TREINTA",
    40: "CUARENTA",
    50: "CINCUENTA",
    60: "SESENTA",
    70: "SETENTA",
    80: "OCHENTA",
    90: "NOVENTA",
  };
  const C = {
    100: "CIENTO",
    200: "DOSCIENTOS",
    300: "TRESCIENTOS",
    400: "CUATROCIENTOS",
    500: "QUINIENTOS",
    600: "SEISCIENTOS",
    700: "SETECIENTOS",
    800: "OCHOCIENTOS",
    900: "NOVECIENTOS",
  };
  const tens = (x) => {
    if (x < 10) return U[x];
    if (x <= 19) return E[x];
    if (x === 20) return "VEINTE";
    if (x < 30) {
      if (x === 22) return "VEINTIDÓS";
      if (x === 23) return "VEINTITRÉS";
      if (x === 26) return "VEINTISÉIS";
      return "VEINTI" + U[x - 20];
    }
    const de = Math.floor(x / 10) * 10;
    const un = x % 10;
    return D[de] + (un ? " Y " + U[un] : "");
  };
  const hundreds = (x) => {
    if (x === 100) return "CIEN";
    const h = Math.floor(x / 100) * 100;
    if (h) return C[h] + (x % 100 ? " " + tens(x % 100) : "");
    return tens(x);
  };
  let s = "";
  if (n >= 1_000_000) {
    const m = Math.floor(n / 1_000_000);
    s += m === 1 ? "UN MILLÓN" : `${numeroATexto(m)} MILLONES`;
    n %= 1_000_000;
    if (n) s += " ";
  }
  if (n >= 1000) {
    const k = Math.floor(n / 1000);
    s += k === 1 ? "MIL" : `${hundreds(k)} MIL`;
    n %= 1000;
    if (n) s += " ";
  }
  if (n > 0) s += hundreds(n);
  return s.trim();
};

const montoALetras = (monto, moneda = "PESOS", sufijo = "MXN") => {
  const totalCents = Math.round(Math.abs(monto) * 100);
  const entero = Math.floor(totalCents / 100);
  const cent = totalCents % 100;
  const fraccion = String(cent).padStart(2, "0");
  let literal = numeroATexto(entero);
  literal = literal.replace(/ UNO$/, " UN").replace(/ VEINTIUNO$/, " VEINTIÚN");
  literal = literal
    .replace(/ VEINTIUNO (?=MIL\b)/g, " VEINTIÚN ")
    .replace(/ UNO (?=MIL\b)/g, " UN ");
  const mon = moneda.toUpperCase();
  const monedaSingular =
    mon === "PESOS"
      ? "PESO"
      : mon === "DÓLARES" || mon === "DOLARES"
      ? "DÓLAR"
      : moneda;
  const usaDe = entero > 0 && entero % 1_000_000 === 0;
  const monedaFinal = entero === 1 ? monedaSingular : moneda;
  const monedaConDe = usaDe ? `DE ${monedaFinal}` : monedaFinal;
  return `${literal} ${monedaConDe} ${fraccion}/100 ${sufijo}`;
};

/* ===== Cliente por ID exacto de la cotización ===== */
const getClienteDeCotizacion = (cot, clientes) => {
  if (!cot) return null;
  const id = cot.cliente || cot.clienteId;
  if (!id) return null;
  return (clientes || []).find((c) => c.id === id || c.__docId === id) || null;
};

/* ===== Normalizadores para dirección ===== */
const clean = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (!s) return "";
  const basura = /^(n\/a|na|n\.?d\.?|\.|-|s\/n|sn|null|undefined)$/i;
  return basura.test(s) ? "" : s;
};
const onlyDigits = (v) => (v || "").replace(/\D+/g, "");

const norm = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const getNombreEmpresa = (c) =>
  c?.empresa || c?.nombre || c?.razonSocial || c?.Nombre || "";

const getDireccionFields = (c) => ({
  calle: clean(c?.calle ?? c?.direccion?.calle),
  numeroExt: clean(
    c?.numeroExt ??
      c?.noExterior ??
      c?.numeroExterior ??
      c?.direccion?.numeroExt
  ),
  numeroInt: clean(
    c?.numeroInt ??
      c?.noInterior ??
      c?.numeroInterior ??
      c?.direccion?.numeroInt
  ),
  colonia: clean(c?.colonia ?? c?.direccion?.colonia),
  ciudad: clean(c?.ciudad ?? c?.direccion?.ciudad),
  estado: clean(c?.estado ?? c?.direccion?.estado),
  codigoPostal: onlyDigits(
    String(c?.codigoPostal ?? c?.cp ?? c?.direccion?.codigoPostal ?? "")
  ),
});

const direccionScore = (d) =>
  (d.calle ? 1 : 0) +
  (d.numeroExt ? 1 : 0) +
  (d.colonia ? 1 : 0) +
  (d.ciudad ? 1 : 0) +
  (d.estado ? 1 : 0) +
  (d.codigoPostal ? 1 : 0);

const makeKey = (f) =>
  [
    norm(
      `${f.calle || ""} ${f.numeroExt || ""}${
        f.numeroInt ? " Int " + f.numeroInt : ""
      }`
    ),
    norm(f.colonia || ""),
    norm(f.ciudad || ""),
    norm(f.estado || ""),
    f.codigoPostal || "",
  ].join("|");

const ResumenCotizacionV1 = ({
  cotizacion,
  isOpen,
  onClose,
  clientes,
  setCotizaciones,
  onClonar,
  cotizaciones,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [pdfVisible, setPdfVisible] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdjuntosModal, setShowAdjuntosModal] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);

  // respaldo: si el cliente no viene en props.clientes, se carga por Firestore
  const [clienteDeFS, setClienteDeFS] = useState(null);

  const navigate = useNavigate();
  const { formatDate } = useFunctionsPrevia();

  const handleConvertirOrden = () => {
    navigate("/pedidos", {
      state: { cotizacion, convertirEnOrden: true },
    });
  };

  useEffect(() => {
    if (showAdjuntosModal && cotizacion?.archivos)
      setAdjuntos(cotizacion.archivos);
  }, [cotizacion, showAdjuntosModal]);

  // Cargar cliente por ID si no está en props.clientes
  useEffect(() => {
    const id = cotizacion?.cliente || cotizacion?.clienteId;
    if (!id) {
      setClienteDeFS(null);
      return;
    }
    const enProp = (clientes || []).find(
      (c) => c.id === id || c.__docId === id
    );
    if (enProp) {
      setClienteDeFS(enProp);
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, "clientes", id));
        setClienteDeFS(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (e) {
        console.error("No pude leer el cliente por id:", e);
        setClienteDeFS(null);
      }
    })();
  }, [cotizacion?.cliente, cotizacion?.clienteId, clientes]);

  const handleDeleteArchivo = async (archivo) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Estás a punto de eliminar el archivo "${archivo.nombre}". Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (result.isConfirmed) {
      try {
        const fileRef = ref(storage, archivo.url);
        await deleteObject(fileRef);
        const cotizacionRef = doc(db, "cotizaciones", cotizacion.id);
        await updateDoc(cotizacionRef, {
          archivos: cotizacion.archivos.filter((a) => a.url !== archivo.url),
        });
        setAdjuntos((prev) => prev.filter((a) => a.url !== archivo.url));
        if (typeof setCotizaciones === "function") {
          setCotizaciones((prev) =>
            prev.map((c) =>
              c.id === cotizacion.id
                ? {
                    ...c,
                    archivos: c.archivos.filter((a) => a.url !== archivo.url),
                  }
                : c
            )
          );
        }
        Swal.fire({
          icon: "success",
          title: "Archivo eliminado",
          text: `El archivo "${archivo.nombre}" ha sido eliminado correctamente.`,
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error al eliminar el archivo:", error);
        Swal.fire({
          icon: "error",
          title: "Error al eliminar",
          text: "Hubo un problema al eliminar el archivo. Por favor, intenta nuevamente.",
        });
      }
    }
  };

  useEffect(() => {
    if (!isOpen && onClose) onClose();
  }, [isOpen, onClose]);

  const memoizedPdfDocument = useMemo(() => {
    if (!cotizacion || !cotizacion.productosSeleccionados?.length) return null;

    // --- 1) Resolución CANÓNICA por nombre (única fuente de verdad) ---
    // Si quieres marcar un cliente "principal" en Firestore, agrega un booleano
    // c.canonico === true (o c.principal === true) y esto lo preferirá.
    const getClienteCanonicoPorNombre = (nombre, lista = []) => {
      const objetivo = norm(nombre);
      const cand = (lista || []).filter(
        (c) => norm(getNombreEmpresa(c)) === objetivo
      );
      if (!cand.length) return null;

      // a) Preferir el marcado como canónico/principal si existe
      const marcado = cand.find(
        (c) => c.canonico || c.principal || c.preferido
      );
      if (marcado) return marcado;

      // b) Desempate determinístico: mayor "completitud" y luego clave estable
      const ordenados = cand
        .map((c) => ({ c, f: getDireccionFields(c) }))
        .sort((a, b) => {
          const sa = direccionScore(a.f);
          const sb = direccionScore(b.f);
          if (sa !== sb) return sb - sa; // más completo primero
          const ka = makeKey(a.f);
          const kb = makeKey(b.f);
          return ka.localeCompare(kb); // clave estable
        });

      return ordenados[0]?.c || null;
    };

    // --- 2) Overrides opcionales por empresa (para forzar mientras limpias datos) ---
    // Puedes borrar esto cuando depures la colección "clientes".
    const ADDRESS_OVERRIDES = {
      "productos maver sa de cv": {
        ciudad: "Guadalajara",
        estado: "Jalisco",
        codigoPostal: "44490",
      },
    };
    const applyOverride = (fields, nombre) => {
      const ov = ADDRESS_OVERRIDES[norm(nombre)];
      return ov
        ? {
            ...fields,
            ...Object.fromEntries(
              Object.entries(ov).map(([k, v]) => [k, clean(v)])
            ),
          }
        : fields;
    };

    // --- 3) Selección del cliente canónico por NOMBRE ---
    const cliById =
      getClienteDeCotizacion(cotizacion, clientes) || clienteDeFS || {};
    const nombreEmpresa =
      cotizacion?.nombreCliente || getNombreEmpresa(cliById);
    const universo = [...(clientes || []), cliById].filter(Boolean); // por si el ID no está en props
    const clienteCanonico =
      getClienteCanonicoPorNombre(nombreEmpresa, universo) || cliById;

    // ÚNICA fuente: dirección del cliente canónico (sin snapshot, sin merges)
    let finalFields = getDireccionFields(clienteCanonico || {});
    finalFields = applyOverride(finalFields, nombreEmpresa);

    const {
      calle,
      numeroExt,
      numeroInt,
      colonia,
      ciudad,
      estado,
      codigoPostal,
    } = finalFields;

    const subtotal = cotizacion.productosSeleccionados.reduce((acc, p) => {
      const precioBruto = (p.tarifa || 0) * (p.cantidad || 0);
      let desc = 0;
      if (p.tipoDescuento === "%")
        desc = precioBruto * ((p.descuento || 0) / 100);
      else if (p.tipoDescuento === "$") desc = p.descuento || 0;
      return acc + (precioBruto - desc);
    }, 0);

    const descuentoPorcentaje = cotizacion.descuento || 0;
    const descuentoAplicado = subtotal * (descuentoPorcentaje / 100);
    const subtotalConDescuento = subtotal - descuentoAplicado;
    const ivaTotal = subtotalConDescuento * 0.16;
    const total = subtotalConDescuento + ivaTotal;

    const divisa = (cotizacion.divisa || "MXN").toUpperCase();
    const monedaPalabra = divisa === "USD" ? "DÓLARES" : "PESOS";
    const totalEnLetras = montoALetras(total, monedaPalabra, divisa);

    // Dirección formateada (sin comas colgantes)
    const linea1Parts = [];
    if (calle) {
      let s = calle;
      if (numeroExt) s += ` ${numeroExt}`;
      if (numeroInt) s += ` Int. ${numeroInt}`;
      linea1Parts.push(s);
    }
    if (colonia) linea1Parts.push(colonia);

    const linea1 = linea1Parts.join(", ");
    const linea2 = [ciudad, estado].filter(Boolean).join(", ");

    const hasLinea1 = !!linea1;
    const hasLinea2 = !!linea2;
    const hasCP = !!codigoPostal;

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          {/* Header (solo aparecerá al inicio; el contenido fluye entre páginas automáticamente) */}
          <View style={styles.header}>
            <View style={styles.logoContainer}>
              <Image src="/img/logo-iciamex2.png" style={styles.logo} />
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.companyName}>
                UNIFICACION PARA LA AUTOMATIZACION SA DE CV
              </Text>
              <Text style={styles.companyDetails}>
                Lic. Ramiro Hernández 448, Las Pintitas
              </Text>
              <Text style={styles.companyDetails}>
                El Salto, Jalisco, Mexico, C.P. 45693
              </Text>
              <Text style={styles.companyDetails}>(33) 1602 2358</Text>
              <Text style={styles.companyDetails}>UAU151222IT1</Text>
            </View>
          </View>

          <View style={styles.titleSection}>
            <Text style={styles.title}>COTIZACIÓN</Text>
          </View>

          <View style={styles.infoContainer}>
            {/* IZQ: Cliente */}
            <View style={styles.clientInfo}>
              <Text style={styles.infoTitle}>Cliente:</Text>
              <Text style={styles.infoTextColor}>
                {cotizacion.nombreCliente ||
                  getNombreEmpresa(clienteCanonico) ||
                  "Nombre del Cliente"}
              </Text>
              {hasLinea1 && <Text style={styles.infoText}>{linea1}</Text>}
              {hasLinea2 && <Text style={styles.infoText}>{`${linea2}.`}</Text>}
              {hasCP && (
                <Text style={styles.infoText}>{`C.P. ${codigoPostal}`}</Text>
              )}
            </View>

            {/* DER: tabla chica */}
            <View style={styles.tableSmall}>
              <View style={styles.tableRowSmall}>
                <Text style={styles.tableLabelSmall}>Cotización #</Text>
                <Text style={styles.tableValueSmall}>
                  {cotizacion.numeroCotizacion?.toString().padStart(4, "0")}
                </Text>
              </View>

              <View style={styles.tableRowSmall}>
                <Text style={styles.tableLabelSmall}>Fecha</Text>
                <Text style={styles.tableValueSmall}>
                  {formatDate(cotizacion.fechaCotizacion)}
                </Text>
              </View>

              {cotizacion.fechaVencimiento && (
                <View style={styles.tableRowSmall}>
                  <Text style={styles.tableLabelSmall}>Vencimiento</Text>
                  <Text style={styles.tableValueSmall}>
                    {formatDate(cotizacion.fechaVencimiento)}
                  </Text>
                </View>
              )}

              {cotizacion.vendedorNombre && (
                <View style={styles.tableRowSmall}>
                  <Text style={styles.tableLabelSmall}>Agente</Text>
                  <Text style={styles.tableValueSmall}>
                    {cotizacion.vendedorNombre}
                  </Text>
                </View>
              )}

              {cotizacion.correoEnvio && (
                <View style={styles.tableRowSmall}>
                  <Text style={styles.tableLabelSmall}>Email</Text>
                  <Text style={styles.tableValueSmall}>
                    {cotizacion.correoEnvio}
                  </Text>
                </View>
              )}

              {cotizacion.atencion && (
                <View style={styles.tableRowSmall}>
                  <Text style={styles.tableLabelSmall}>Atención</Text>
                  <Text style={styles.tableValueSmall}>
                    {cotizacion.atencion}
                  </Text>
                </View>
              )}

              {cotizacion.asunto && (
                <View style={[styles.tableRowSmall, styles.rowTight]}>
                  <View style={[styles.tableLabelBox, styles.labelTightBoxSm]}>
                    <Text style={styles.tableLabelSmallText}>Asunto</Text>
                  </View>
                  <Text style={[styles.tableValueSmall, styles.valueTightSm]}>
                    {partirEnDosLineas(cotizacion.asunto)}
                  </Text>
                </View>
              )}

              {cotizacion.planta && (
                <View style={styles.tableRowSmall}>
                  <Text style={styles.tableLabelSmall}>Planta</Text>
                  <Text style={styles.tableValueSmall}>
                    {cotizacion.planta}
                  </Text>
                </View>
              )}

              {cotizacion.area && (
                <View style={styles.tableRowSmall}>
                  <Text style={styles.tableLabelSmall}>Área</Text>
                  <Text style={styles.tableValueSmall}>{cotizacion.area}</Text>
                </View>
              )}
            </View>
          </View>

          <Text style={styles.infoBlessed}>
            Gracias por darnos la oportunidad de hacerle una propuesta de
            nuestros equipos y/o servicios. Estamos seguros que quedará
            satisfecho con los beneficios que ofrecemos.
          </Text>

          {/* Tabla de productos (sin paginado manual) */}
          <View style={styles.table}>
            <View style={styles.tableRowHeader}>
              <Text style={[styles.tableCell, { flex: 0.1 }]}>#</Text>
              <Text style={[styles.tableCell, { flex: 5 }]}>
                Artículo & Descripción
              </Text>
              <Text style={[styles.tableCell, { flex: 1.6 }]}>Modelo</Text>
              <Text style={[styles.tableCell, { flex: 0.5 }]}>Cant.</Text>
              <Text style={[styles.tableCell, { flex: 1.2 }]}>
                Precio Unitario
              </Text>
              <Text style={[styles.tableCell, { flex: 1 }]}>Importe</Text>
            </View>

            {cotizacion.productosSeleccionados.map((producto, index) => {
              const descripcionCompleta =
                producto.descripcion || producto.nombre || "";
              const [titulo, ...resto] = descripcionCompleta.split("\n");

              return (
                // ⬅ wrap={false} evita que la fila se parta entre páginas
                <View key={index} style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 0.1 }]}>
                    {index + 1}
                  </Text>

                  <Text style={[styles.tableCellDesc, { flex: 5 }]}>
                    <Text style={styles.descTitle}>{titulo}</Text>
                    {resto.length > 0 && (
                      <Text style={styles.descExtra}>
                        {"\n"}
                        {resto.join("\n")}
                      </Text>
                    )}
                  </Text>

                  <Text style={[styles.tableCell, { flex: 1.6 }]}>
                    {producto.modelo || producto.numeroDeParteCliente || ""}
                  </Text>
                  <Text style={[styles.tableCell, { flex: 0.5 }]}>
                    {producto.cantidad}
                  </Text>
                  <Text style={[styles.tableCell, { flex: 1.2 }]}>
                    {parseFloat(producto.tarifa || 0)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>
                    {parseFloat(producto.importe || 0)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </Text>
                </View>
              );
            })}

            {/* Totales (se mantienen juntos; si no caben, pasan completos a la siguiente página) */}
            <View style={styles.footerBlock} wrap={false}>
              {/* === FILA 1: SOLO TOTALES A LA DERECHA === */}
              <View style={styles.totalsRow}>
                {/* Columna izquierda vacía para respetar la cuadrícula */}
                <View style={{ width: "58%" }} />
                <View style={styles.footerRight}>
                  <View style={[styles.footerRow, styles.footerRowGray]}>
                    <View style={styles.footerLabelContainer}>
                      <Text style={styles.footerLabel}>Subtotal</Text>
                    </View>
                    <View style={styles.footerValueContainer}>
                      <Text style={styles.footerValue}>
                        $
                        {subtotal
                          .toFixed(2)
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </Text>
                    </View>
                  </View>

                  {descuentoAplicado > 0 && (
                    <>
                      <View style={[styles.footerRow, styles.footerRowGray]}>
                        <View style={styles.footerLabelContainer}>
                          <Text style={styles.footerLabel}>
                            Descuento ({descuentoPorcentaje}%)
                          </Text>
                        </View>
                        <View style={styles.footerValueContainer}>
                          <Text style={styles.footerValue}>
                            -$
                            {descuentoAplicado
                              .toFixed(2)
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                          </Text>
                        </View>
                      </View>

                      <View style={[styles.footerRow, styles.footerRowGray]}>
                        <View style={styles.footerLabelContainer}>
                          <Text style={styles.footerLabel}>Restante</Text>
                        </View>
                        <View style={styles.footerValueContainer}>
                          <Text style={styles.footerValue}>
                            $
                            {subtotalConDescuento
                              .toFixed(2)
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                          </Text>
                        </View>
                      </View>
                    </>
                  )}

                  <View style={[styles.footerRow, styles.footerRowGray]}>
                    <View style={styles.footerLabelContainer}>
                      <Text style={styles.footerLabel}>IVA (16%)</Text>
                    </View>
                    <View style={styles.footerValueContainer}>
                      <Text style={styles.footerValue}>
                        $
                        {ivaTotal
                          .toFixed(2)
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </Text>
                    </View>
                  </View>

                  <View style={[styles.footerRow, styles.footerRowBlue]}>
                    <View style={styles.footerLabelContainer}>
                      <Text
                        style={[styles.footerLabel, styles.footerLabelBlue]}
                      >
                        Total
                      </Text>
                    </View>
                    <View style={styles.footerValueContainer}>
                      <Text
                        style={[styles.footerValue, styles.footerValueBlue]}
                      >
                        $
                        {total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
                        {cotizacion.divisa}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>

              {/* === FILA 2: IZQ TIEMPO/CONDICIONES  —  DER IMPORTE EN LETRAS === */}
              <View style={styles.lettersRow}>
                <View
                  style={[
                    styles.sectionBlock,
                    styles.sectionBlockReset,
                    styles.entregaBox,
                  ]}
                >
                  <Text style={styles.sectionHeading}>TIEMPO DE ENTREGA:</Text>
                  {(() => {
                    const raw = (cotizacion.tiempoEntrega || "")
                      .toString()
                      .replace(/\r/g, "")
                      .trim();
                    if (!raw) return <Text style={styles.entregaLine}>—</Text>;
                    const lines = raw.includes("\n")
                      ? raw.split(/\n+/).filter(Boolean)
                      : [raw];
                    return (
                      <View style={styles.entregaList}>
                        {lines.map((l, i) => (
                          <Text
                            key={i}
                            style={styles.entregaLine}
                            hyphenationCallback={(w) => [w]}
                          >
                            {l}
                          </Text>
                        ))}
                      </View>
                    );
                  })()}

                  <Text style={styles.sectionHeading}>
                    CONDICIONES DE PAGO:
                  </Text>
                  {(() => {
                    const raw = (cotizacion.condicionesPago || "")
                      .toString()
                      .replace(/\r/g, "")
                      .trim();
                    if (!raw) return <Text style={styles.entregaLine}>—</Text>;
                    const lines = raw.split(/\n+/).filter(Boolean);
                    return (
                      <View style={styles.entregaList}>
                        {lines.map((l, i) => (
                          <Text
                            key={i}
                            style={styles.entregaLine}
                            hyphenationCallback={(w) => [w]}
                          >
                            {l}
                          </Text>
                        ))}
                      </View>
                    );
                  })()}
                </View>

                <View style={styles.montoGroupRight}>
                  <Text style={styles.montoEnLetrasLabel}>
                    IMPORTE TOTAL CON LETRA
                  </Text>
                  <Text style={styles.montoEnLetras}>{totalEnLetras}</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Footer fijo con número de página */}
          <View
            fixed
            style={{
              position: "absolute",
              bottom: 10,
              left: 20,
              right: 30,
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                fontSize: 10,
                textAlign: "left",
                marginLeft: 10,
                color: "#999999",
              }}
            >
              UNIFICACION PARA LA AUTOMATIZACION SA DE CV
            </Text>
            <Text
              style={styles.numeroDePagina}
              render={({ pageNumber }) => `${pageNumber}`}
            />
          </View>
        </Page>

        {/* (Opcional) tu “página extra” si realmente la necesitas */}
        {/* <Page size="A4" orientation="landscape" style={styles.page}> ... </Page> */}
      </Document>
    );
  }, [cotizacion, clientes, clienteDeFS, formatDate]);

  useEffect(() => {
    let isMounted = true;
    let url;
    const generar = async () => {
      if (!memoizedPdfDocument) {
        setPdfBlobUrl(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const blob = await pdf(memoizedPdfDocument).toBlob();
        if (!isMounted) return;
        url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
      } catch (err) {
        console.error("Error generando PDF:", err);
        if (isMounted) setPdfBlobUrl(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    generar();
    return () => {
      isMounted = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [memoizedPdfDocument]);

  const navigateToClone = async () => {
    try {
      const docRef = doc(db, "configuracion", "numeroCotizacion");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const { prefijo = "IC-", siguienteNumero = "1" } = data;
        const posibleNumero = `${prefijo}${siguienteNumero}`;

        const clon = {
          ...cotizacion,
          fechaCotizacion: new Date(),
          numeroCotizacion: posibleNumero,
          resumenVersion: cotizacion?.resumenVersion ?? 1,
        };

        delete clon.id;
        if (typeof onClonar === "function") onClonar(clon);
        if (onClose) onClose();
      } else {
        console.error("No se encontró la config de numeroCotizacion");
      }
    } catch (error) {
      console.error("Error al clonar la cotización:", error);
      Swal.fire({
        icon: "error",
        title: "Error al clonar",
        text: "Hubo un problema al clonar la cotización. Intenta nuevamente.",
      });
    }
  };

  const handleEliminar = async () => {
    const result = await Swal.fire({
      title: `¿Seguro que quieres eliminar la cotización "${cotizacion.numeroCotizacion}"?`,
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (result.isConfirmed) {
      if (onClose) onClose();
      try {
        await deleteDoc(doc(db, "cotizaciones", cotizacion.id));
        if (setCotizaciones) {
          setCotizaciones((prev) => prev.filter((c) => c.id !== cotizacion.id));
        }
        await Swal.fire({
          icon: "success",
          title: "Eliminado",
          text: `La cotización "${cotizacion.numeroCotizacion}" ha sido eliminada correctamente.`,
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error al eliminar la cotización:", error);
        Swal.fire({
          icon: "error",
          title: "Error al eliminar",
          text: "No se pudo eliminar la cotización. Intenta de nuevo.",
        });
      }
    }
  };

  const handlePrint = () => setPdfVisible(true);
  const handleClosePDF = () => setPdfVisible(false);

  const handleEdit = () => {
    setEditMode(true);
    setShowSummary(false);
  };

  const closeModal = () => {
    setEditMode(false);
    setShowSummary(true);
    onClose();
  };

  const obtenerIconoArchivo = (nombreArchivo) => {
    const extension = nombreArchivo.split(".").pop().toLowerCase();
    switch (extension) {
      case "xls":
      case "xlsx":
      case "csv":
        return { icon: "bi-file-earmark-excel", color: "green" };
      case "doc":
      case "docx":
        return { icon: "bi-file-earmark-word", color: "blue" };
      case "pdf":
        return { icon: "bi-file-earmark-pdf", color: "red" };
      case "jpg":
      case "jpeg":
      case "png":
      case "svg":
        return { icon: "bi-image", color: "blue" };
      default:
        return { icon: "bi-file-earmark", color: "gray" };
    }
  };

  if (editMode) {
    return (
      <EditarCotizacionForm
        cotizacion={cotizacion}
        clientes={clientes}
        productos={cotizacion && cotizacion.productosSeleccionados}
        setCotizaciones={setCotizaciones}
        cotizaciones={cotizaciones}
        onClose={closeModal}
      />
    );
  }

  if (pdfVisible) {
    return (
      <div style={styles.pdfContainer}>
        <button
          className="cerrar-button"
          style={styles.closeButton}
          onClick={handleClosePDF}
        >
          <i className="bi bi-x-lg"></i>
        </button>
        <PDFViewer className="pdf-viewer" style={styles.pdfViewer}>
          {memoizedPdfDocument}
        </PDFViewer>
      </div>
    );
  }

  if (showSummary && cotizacion && cotizacion.productosSeleccionados) {
    return (
      <div className="resumen-cotizacion-container">
        {/* Header: izquierda = Regresar / derecha = botones azules */}
        <div className="resumen-cotizacion-header">
          <button
            className="back-resumen-btn"
            onClick={onClose}
            title="Regresar a la tabla"
          >
            <i className="bi bi-arrow-left-short"></i>
            <span>Regresar a la tabla</span>
          </button>

          <div className="resumen-cotizacion-actions">
            {/* Dropdown Imprimir / PDF */}
            <div className="dropdown">
              <button className="dropbtn">
                <i className="bi bi-printer iconResumen"></i> Imprimir / PDF
              </button>

              <div className="dropdown-content">
                <button onClick={handlePrint}>
                  <i
                    className="bi bi-file-earmark-text"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Imprimir
                </button>

                <a
                  href={pdfBlobUrl || "#"}
                  download={`${cotizacion.numeroCotizacion || "sin_numero"}_${
                    cotizacion.asunto || "sin_asunto"
                  }.pdf`}
                  style={{ textDecoration: "none" }}
                  onClick={(e) => {
                    if (!pdfBlobUrl) e.preventDefault();
                  }}
                >
                  <button>
                    <i
                      className="bi bi-download"
                      style={{ marginRight: "10px" }}
                    ></i>
                    Descargar PDF
                  </button>
                </a>
              </div>
            </div>

            {/* Botón simple: Correo */}
            <button>
              <i className="bi bi-envelope-fill iconResumen"></i> Correo
            </button>

            {/* Botón simple: Editar */}
            <button onClick={handleEdit}>
              <i className="bi bi-pencil-fill iconResumen"></i> Editar
            </button>

            {/* Dropdown Convertir en */}
            <div className="dropdown">
              <button className="dropbtn">
                <i className="bi bi-file-earmark-text iconResumen"></i>{" "}
                Convertir en
              </button>

              <div className="dropdown-content">
                <button onClick={handleConvertirOrden}>
                  <i
                    className="bi bi-file-text"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Pedido
                </button>
                <button>
                  <i
                    className="bi bi-receipt"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Factura
                </button>
              </div>
            </div>

            <div className="dropdown dropdown--right dropdown--icon">
              <button className="dropbtn">
                <i
                  className="bi bi-three-dots-vertical iconResumen"
                  style={{ marginRight: 0 }}
                ></i>
              </button>

              <div className="dropdown-content">
                <button onClick={handleEliminar}>
                  <i
                    className="bi bi-trash"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Eliminar
                </button>
                <button onClick={navigateToClone}>
                  <i
                    className="bi bi-files"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Clonar
                </button>
              </div>
            </div>

            {/* Botón Archivos Adjuntos (no es dropdown) */}
            {cotizacion.archivos && cotizacion.archivos.length > 0 && (
              <button onClick={() => setShowAdjuntosModal(true)}>
                <i className="bi bi-paperclip iconResumen"></i> Archivos
                Adjuntos
              </button>
            )}
          </div>
        </div>

        <Modal
          isOpen={showAdjuntosModal}
          onRequestClose={() => setShowAdjuntosModal(false)}
          contentLabel="Archivos Adjuntos"
          style={{
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
              width: "35%",
              maxHeight: "80vh",
              overflowY: "auto",
              padding: "20px",
              borderRadius: "10px",
            },
            overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
          }}
        >
          <button
            onClick={() => setShowAdjuntosModal(false)}
            className="modal-close"
            title="Cerrar"
          >
            <i className="bi bi-x-lg"></i>
          </button>

          <h3
            style={{
              marginBottom: "20px",
              color: "#000",
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Archivos Adjuntos
          </h3>

          <ul style={{ listStyle: "none", padding: 0 }}>
            {adjuntos.map((archivo, index) => {
              const { icon, color } = obtenerIconoArchivo(archivo.nombre);
              return (
                <li
                  key={index}
                  style={{
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <i
                      className={`bi ${icon}`}
                      style={{ color, fontSize: "1.5rem", marginRight: 10 }}
                    />
                    <a
                      href={archivo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: "#000",
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {archivo.nombre}
                    </a>
                  </div>

                  <div style={{ display: "flex", alignItems: "center" }}>
                    <a href={archivo.url} download={archivo.nombre}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          const link = document.createElement("a");
                          link.href = archivo.url;
                          link.target = "_blank";
                          link.rel = "noopener noreferrer";
                          link.download = archivo.nombre;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#007bff",
                          cursor: "pointer",
                          fontSize: 20,
                        }}
                        title="Descargar archivo"
                      >
                        <i className="bi bi-download"></i>
                      </button>
                    </a>
                    <button
                      onClick={() => handleDeleteArchivo(archivo)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        marginRight: 10,
                        fontSize: 20,
                      }}
                      title="Eliminar archivo"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Modal>

        <div
          style={{
            height: "600px",
            width: "100%",
            marginTop: "50px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {loading ? (
            <div className="loader2"></div>
          ) : (
            pdfBlobUrl && (
              <iframe
                src={`${pdfBlobUrl}#toolbar=0`}
                style={{ width: "100%", height: "100%" }}
                frameBorder="0"
                title="Vista previa del PDF"
              />
            )
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default ResumenCotizacionV1;
